import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const pickupAddressAdapter = createEntityAdapter({
    selectId: (address) => address._id,
    sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
});

// Async Thunks
export const fetchPickupAddresses = createAsyncThunk(
    "pickupAddress/fetch",
    async ({ userId }, { rejectWithValue }) => {
        try {
            // API request using params
            const response = await request({
                endpoint: "/admin/customer/getPickupAddress",
                method: "GET",
                params: { userId }, // Pass userId as query param
            });

            // Debugging: Log response
            console.log("Pickup Addresses API Response:", response);

            // Validate response structure
            if (!response.data || !Array.isArray(response.data.pickupAddresses)) {
                return rejectWithValue(response.error?.message || "Failed to fetch pickup addresses.");
            }

            // Return pickupAddresses array
            return response.data.pickupAddresses;
        } catch (error) {
            return rejectWithValue(error.message || "An unexpected error occurred while fetching pickup addresses.");
        }
    }
);

// Initial state
const initialState = pickupAddressAdapter.getInitialState({
    loading: false,
    error: null,
});

// Slice
const pickupAddressSlice = createSlice({
    name: "pickupAddress",
    initialState,
    reducers: {
        addPickupAddresses: (state, action) => {
            pickupAddressAdapter.setAll(state, action.payload);
        },
        clearPickupAddresses: (state) => {
            pickupAddressAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
        },

    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPickupAddresses.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPickupAddresses.fulfilled, (state, action) => {
                pickupAddressAdapter.setAll(state, action.payload);
                state.loading = false;
            })
            .addCase(fetchPickupAddresses.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

    },
});

// Selectors
export const {
    selectAll: selectAllPickupAddresses,
    selectById: selectPickupAddressById,
    selectIds: selectPickupAddressIds,
} = pickupAddressAdapter.getSelectors((state) => state.pickupAddress);

// Actions & Reducer Export
export const { clearPickupAddresses, addPickupAddresses, } = pickupAddressSlice.actions;
export default pickupAddressSlice.reducer;